import { useEffect, useState } from 'react';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import type { Intervention, Mechanic } from '@/lib/types';
import RdvCard from './RdvCard';
import { useUpdateSlot, useUpdateDuration } from '@/hooks/useInterventionMutations';
import { useAuthStore } from '@/stores/authStore';
import { useAtelierConfig } from '@/hooks/useAtelierConfig';
import { formatDurationMinutes, maintenantMurCasablanca } from '@/lib/dates';

const PIXELS_PER_MINUTE = 1.5;
const SLOT_GRANULARITY_MIN = 15;
// Repli utilisé tant que la config atelier n'est pas chargée (ou en erreur).
const DEFAULT_DAY_START_HOUR = 9;
const DEFAULT_DAY_END_HOUR = 18;

type Props = {
  mechanics: Mechanic[];
  interventions: Intervention[];
  date: string;
  onClickIntervention?: (i: Intervention) => void;
};

function minutesFromDayStart(d: Date | string, dayStartHour: number): number {
  const date = typeof d === 'string' ? new Date(d) : d;
  return (date.getUTCHours() - dayStartHour) * 60 + date.getUTCMinutes();
}

export default function PlanningGrid({ mechanics, interventions, date, onClickIntervention }: Props) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const updateSlot = useUpdateSlot();
  const [error, setError] = useState<string | null>(null);
  const userRole = useAuthStore((s) => s.user?.role);
  const canResize = userRole === 'admin' || userRole === 'atelier';

  // Horaires atelier réels (config admin) ; repli sur 9h-18h tant que non chargés.
  const { data: config } = useAtelierConfig();
  const dayStartHour = config?.dayStartHour ?? DEFAULT_DAY_START_HOUR;
  const dayEndHour = config?.dayEndHour ?? DEFAULT_DAY_END_HOUR;
  const totalHours = Math.max(1, dayEndHour - dayStartHour);
  const totalMinutes = totalHours * 60;
  const gridHeightPx = totalMinutes * PIXELS_PER_MINUTE;

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;

    const interventionId = active.id as string;
    const intervention = interventions.find((i) => i.id === interventionId);
    if (!intervention) return;

    const overId = over.id as string;
    const [targetMecanoId, targetSlotStr] = overId.split(':');
    if (!targetMecanoId || !targetSlotStr) return;

    const targetMinutes = Number(targetSlotStr);
    if (Number.isNaN(targetMinutes)) return;

    const [year, month, day] = date.split('-').map(Number);
    const hours = dayStartHour + Math.floor(targetMinutes / 60);
    const minutes = targetMinutes % 60;
    const targetDate = new Date(Date.UTC(year!, month! - 1, day!, hours, minutes, 0, 0));

    // Skip if same slot + same mecano
    const currentDebutMin = minutesFromDayStart(intervention.creneauDebut, dayStartHour);
    if (targetMecanoId === intervention.mecanicienId && targetMinutes === currentDebutMin) {
      return;
    }

    setError(null);
    try {
      await updateSlot.mutateAsync({
        id: interventionId,
        creneauDebut: targetDate.toISOString(),
        mecanicienId: targetMecanoId,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Conflit';
      setError(message);
      setTimeout(() => setError(null), 3000);
    }
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      {error && (
        <div className="mb-3 px-4 py-2 rounded-md bg-status-annule-bg text-status-annule-text text-sm">
          {error}
        </div>
      )}

      <div className="bg-surface-white border border-border-light rounded-lg overflow-hidden">
        <div
          className="grid relative"
          style={{ gridTemplateColumns: `60px repeat(${mechanics.length}, 1fr)` }}
        >
          {/* Header row */}
          <div className="border-b border-border-light bg-surface-bg h-[60px]" />
          {mechanics.map((m) => {
            const count = interventions.filter((i) => i.mecanicienId === m.id).length;
            return (
              <div
                key={m.id}
                className="border-b border-l border-border-light px-4 py-3 flex items-center gap-3"
              >
                <span
                  className="w-7 h-7 rounded-sm flex items-center justify-center text-text-on-orange font-bold text-sm shrink-0"
                  style={{ backgroundColor: m.couleur }}
                  aria-hidden="true"
                >
                  {m.prenom.charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <div className="text-base font-semibold text-text-primary truncate">
                    {m.prenom} {m.nom}
                  </div>
                  <div className="text-xs text-text-muted font-mono">{count} RDV</div>
                </div>
              </div>
            );
          })}

          {/* Time column */}
          <div className="relative" style={{ height: gridHeightPx }}>
            {Array.from({ length: totalHours * 2 + 1 }).map((_, idx) => {
              const halfHour = dayStartHour + idx * 0.5;
              const top = (halfHour - dayStartHour) * 60 * PIXELS_PER_MINUTE;
              const isHour = Number.isInteger(halfHour);
              if (!isHour) return null;
              return (
                <div
                  key={idx}
                  className="absolute right-2 -translate-y-1/2 text-xs font-mono text-text-muted"
                  style={{ top }}
                >
                  {`${String(halfHour).padStart(2, '0')}:00`}
                </div>
              );
            })}
          </div>

          {/* Mechanic columns */}
          {mechanics.map((m) => (
            <MechanicColumn
              key={m.id}
              mecano={m}
              interventions={interventions.filter((i) => i.mecanicienId === m.id)}
              onClickIntervention={onClickIntervention}
              canResize={canResize}
              dayStartHour={dayStartHour}
              dayEndHour={dayEndHour}
              totalMinutes={totalMinutes}
              gridHeightPx={gridHeightPx}
            />
          ))}

          {/* Ligne "maintenant" — overlay span sur la row contenu, visible si date === aujourd'hui */}
          <CurrentTimeLine
            date={date}
            columnsCount={mechanics.length}
            dayStartHour={dayStartHour}
            totalMinutes={totalMinutes}
          />
        </div>
      </div>
    </DndContext>
  );
}

/**
 * Ligne horizontale rouge traversant les colonnes mécaniciens à l'heure courante.
 * Visible uniquement quand on affiche le planning d'aujourd'hui (date === today).
 * Positionnée via grid-column span pour rester alignée avec les colonnes.
 * Mise à jour chaque minute.
 */
function CurrentTimeLine({
  date,
  columnsCount,
  dayStartHour,
  totalMinutes,
}: {
  date: string;
  columnsCount: number;
  dayStartHour: number;
  totalMinutes: number;
}) {
  const [now, setNow] = useState(maintenantMurCasablanca);

  useEffect(() => {
    const id = setInterval(() => setNow(maintenantMurCasablanca()), 60_000);
    return () => clearInterval(id);
  }, []);

  if (date !== now.toISOString().slice(0, 10)) return null;

  const totalMin = (now.getUTCHours() - dayStartHour) * 60 + now.getUTCMinutes();
  if (totalMin < 0 || totalMin > totalMinutes) return null;

  const top = totalMin * PIXELS_PER_MINUTE;
  const label = `${String(now.getUTCHours()).padStart(2, '0')}:${String(now.getUTCMinutes()).padStart(2, '0')}`;

  // Span sur la 2e row du grid (row 2 = contenu), toutes colonnes (1 + columnsCount)
  return (
    <div
      className="relative pointer-events-none"
      style={{
        gridRow: 2,
        gridColumn: `1 / span ${columnsCount + 1}`,
        height: 0,
        zIndex: 20,
      }}
    >
      {/* Trait rouge sur la zone des colonnes mécaniciens (à partir du début col 2) */}
      <div
        className="absolute right-0"
        style={{ top, left: 60, borderTop: '2px solid #DC2626' }}
      />
      {/* Dot + label dans la colonne temps (largeur 60px) */}
      <div
        className="absolute left-0 flex items-center justify-end gap-1 pr-2 -translate-y-1/2"
        style={{ top, width: 60 }}
      >
        <span className="text-xs font-mono font-semibold text-status-annule-text">
          {label}
        </span>
        <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: '#DC2626' }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

type ColumnProps = {
  mecano: Mechanic;
  interventions: Intervention[];
  onClickIntervention?: (i: Intervention) => void;
  canResize: boolean;
  dayStartHour: number;
  dayEndHour: number;
  totalMinutes: number;
  gridHeightPx: number;
};

function MechanicColumn({
  mecano,
  interventions,
  onClickIntervention,
  canResize,
  dayStartHour,
  dayEndHour,
  totalMinutes,
  gridHeightPx,
}: ColumnProps) {
  const dropZones: number[] = [];
  for (let m = 0; m < totalMinutes; m += SLOT_GRANULARITY_MIN) {
    dropZones.push(m);
  }

  return (
    <div className="relative border-l border-border-light" style={{ height: gridHeightPx }}>
      {/* Lines (every 15 min) */}
      {Array.from({ length: totalMinutes / SLOT_GRANULARITY_MIN }).map((_, idx) => {
        const top = idx * SLOT_GRANULARITY_MIN * PIXELS_PER_MINUTE;
        const isHour = idx % 4 === 0;
        return (
          <div
            key={idx}
            className={`absolute left-0 right-0 ${
              isHour ? 'border-t border-border-light' : 'border-t border-surface-divider'
            }`}
            style={{ top }}
          />
        );
      })}

      {/* Droppable zones (per 15 min slot, plage continue selon config atelier) */}
      {dropZones.map((m) => (
        <DroppableSlot key={m} mecanoId={mecano.id} minutes={m} />
      ))}

      {/* Draggable interventions */}
      {interventions.map((i) => (
        <DraggableCard
          key={i.id}
          intervention={i}
          canResize={canResize}
          onClick={() => onClickIntervention?.(i)}
          dayStartHour={dayStartHour}
          dayEndHour={dayEndHour}
        />
      ))}
    </div>
  );
}

function DroppableSlot({ mecanoId, minutes }: { mecanoId: string; minutes: number }) {
  const { setNodeRef, isOver } = useDroppable({ id: `${mecanoId}:${minutes}` });
  const top = minutes * PIXELS_PER_MINUTE;
  const height = SLOT_GRANULARITY_MIN * PIXELS_PER_MINUTE;
  return (
    <div
      ref={setNodeRef}
      className={`absolute left-0 right-0 ${isOver ? 'bg-ktm-orange-light' : ''}`}
      style={{ top, height }}
    />
  );
}

function DraggableCard({
  intervention,
  canResize,
  onClick,
  dayStartHour,
  dayEndHour,
}: {
  intervention: Intervention;
  canResize: boolean;
  onClick: () => void;
  dayStartHour: number;
  dayEndHour: number;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: intervention.id,
  });
  const updateDuration = useUpdateDuration();
  const [previewDuration, setPreviewDuration] = useState<number | null>(null);

  const effectiveDuration = previewDuration ?? intervention.dureeEstimeeMinutes;
  const top = minutesFromDayStart(intervention.creneauDebut, dayStartHour) * PIXELS_PER_MINUTE;
  const height = effectiveDuration * PIXELS_PER_MINUTE - 4;

  const isResizing = previewDuration !== null;

  const style: React.CSSProperties = {
    top: top + 2,
    height,
    transform: !isResizing && transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging && !isResizing ? 0.5 : 1,
    zIndex: isDragging || isResizing ? 50 : 1,
  };

  // Pendant le resize, on désactive les listeners dnd-kit (le drag de déplacement)
  const dragProps = isResizing ? {} : { ...attributes, ...listeners };

  return (
    <div ref={setNodeRef} {...dragProps} className="absolute left-2 right-2" style={style}>
      <RdvCard
        intervention={intervention}
        onClick={onClick}
        style={{ height: '100%', overflow: 'hidden' }}
      />
      {canResize && (
        <ResizeHandle
          interventionId={intervention.id}
          startDuration={intervention.dureeEstimeeMinutes}
          startCreneauDebut={intervention.creneauDebut}
          dayEndHour={dayEndHour}
          previewDuration={previewDuration}
          onPreview={setPreviewDuration}
          onCommit={(finalDuration) => {
            setPreviewDuration(null);
            if (finalDuration !== intervention.dureeEstimeeMinutes) {
              updateDuration.mutate({ id: intervention.id, dureeEstimeeMinutes: finalDuration });
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * Poignée de redimensionnement en bas de la carte (CDC §9.1 Méthode 1).
 * Drag vertical → ajuste la durée par tranches de 15 min (snap).
 *  - Vers le bas : étend l'intervention
 *  - Vers le haut : raccourcit l'intervention (min 15 min)
 * Au release : appelle PATCH /interventions/:id/duration → cascade backend.
 * Permission : Hamza (atelier) + admin uniquement.
 */
function ResizeHandle({
  startDuration,
  startCreneauDebut,
  dayEndHour,
  previewDuration,
  onPreview,
  onCommit,
}: {
  interventionId: string;
  startDuration: number;
  startCreneauDebut: string;
  dayEndHour: number;
  previewDuration: number | null;
  onPreview: (duration: number | null) => void;
  onCommit: (duration: number) => void;
}) {
  const [hovering, setHovering] = useState(false);

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    // Bloque dnd-kit AVANT toute autre chose (capture phase)
    e.stopPropagation();
    e.preventDefault();

    const handleElem = e.currentTarget;
    const pointerId = e.pointerId;

    // setPointerCapture : verrouille tous les events pointer suivants sur cet élément,
    // peu importe où va la souris. Évite que dnd-kit (ou un autre handler) intercepte.
    try {
      handleElem.setPointerCapture(pointerId);
    } catch {
      // pointerId déjà capturé ailleurs — on continue quand même
    }

    const startY = e.clientY;
    const baseDuration = startDuration;
    let lastDuration = startDuration;

    const debut = new Date(startCreneauDebut);
    const endOfDay = new Date(debut);
    endOfDay.setUTCHours(dayEndHour, 0, 0, 0);
    const maxDuration = Math.max(
      15,
      Math.floor((endOfDay.getTime() - debut.getTime()) / 60_000),
    );

    onPreview(baseDuration);

    function handleMove(ev: PointerEvent) {
      if (ev.pointerId !== pointerId) return;
      const deltaPx = ev.clientY - startY;
      const deltaMin = Math.round(deltaPx / (15 * PIXELS_PER_MINUTE)) * 15;
      const newDuration = Math.min(maxDuration, Math.max(15, baseDuration + deltaMin));
      if (newDuration !== lastDuration) {
        lastDuration = newDuration;
        onPreview(newDuration);
      }
    }

    function handleUp(ev: PointerEvent) {
      if (ev.pointerId !== pointerId) return;
      handleElem.removeEventListener('pointermove', handleMove);
      handleElem.removeEventListener('pointerup', handleUp);
      handleElem.removeEventListener('pointercancel', handleUp);
      try {
        handleElem.releasePointerCapture(pointerId);
      } catch {
        // already released
      }
      document.body.style.userSelect = '';
      onCommit(lastDuration);
    }

    // Empêche la sélection de texte pendant le drag
    document.body.style.userSelect = 'none';
    // Listeners attachés à l'élément (pas window) — grâce à setPointerCapture, ils
    // recevront TOUS les pointer-events, même si la souris quitte la zone du handle.
    handleElem.addEventListener('pointermove', handleMove);
    handleElem.addEventListener('pointerup', handleUp);
    handleElem.addEventListener('pointercancel', handleUp);
  }

  const active = hovering || previewDuration !== null;
  const currentDuration = previewDuration ?? startDuration;

  return (
    <div
      className="absolute bottom-0 left-0 right-0 h-4 cursor-ns-resize z-10 group flex items-end justify-center"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onPointerDown={handlePointerDown}
      onClick={(e) => e.stopPropagation()}
      title="Tirer vers le haut pour réduire, vers le bas pour étendre"
    >
      {/* Indicateur visuel : barre épaisse + 2 traits, plus visible */}
      <div className="pb-1 flex items-center justify-center gap-0.5">
        <div
          className={`h-0.5 rounded-full transition-all ${
            active ? 'w-12 bg-ktm-orange' : 'w-10 bg-text-muted/40 group-hover:bg-text-muted/70'
          }`}
        />
      </div>

      {/* Tooltip durée pendant le drag */}
      {previewDuration !== null && (
        <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 px-2 py-1 bg-text-primary text-text-on-orange text-xs font-mono font-semibold rounded-sm whitespace-nowrap shadow-md z-50 pointer-events-none">
          {formatDurationMinutes(currentDuration)}
        </div>
      )}
    </div>
  );
}
